'use client';

import { type ReactNode, useEffect } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import {
  useCookiebotStatisticsConsent,
  useCookiebotStatisticsDeclined,
} from '@/hooks/use-cookiebot-statistics-consent';

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
let posthogInitialized = false;

export function CSPostHogProvider({ children }: { children: ReactNode }) {
  const hasStatisticsConsent = useCookiebotStatisticsConsent();
  const hasExplicitlyDeclined = useCookiebotStatisticsDeclined();

  // Initialize PostHog unconditionally (capturing on by default).
  // Only opt out when a user explicitly declines statistics cookies.
  useEffect(() => {
    if (!posthogKey || !posthogHost) {
      console.warn('PostHog key or host not set. PostHog will not be initialized.');
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (!posthogInitialized) {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        person_profiles: 'identified_only',
        capture_performance: true,
      });
      posthogInitialized = true;
    }

    if (hasExplicitlyDeclined) {
      // User actively declined statistics cookies — drop the locally stored
      // identity, then stop capturing. reset() must come first: it clears the
      // persisted consent state, so an opt-out issued before it would be undone.
      posthog.reset();
      posthog.opt_out_capturing();
    } else if (hasStatisticsConsent) {
      // User explicitly accepted — ensure capturing is on
      // (handles the case where they previously declined then re-accepted).
      posthog.opt_in_capturing();
    }
    // Default case (no banner interaction): capturing stays on via PostHog defaults.
  }, [hasStatisticsConsent, hasExplicitlyDeclined]);

  if (!posthogKey || !posthogHost) {
    return <>{children}</>;
  }

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
